const ModelEvaluationPost = () => {
  const metadata = {
    id: 1,
    slug: "production-ai-evaluation",
    title: "Production AI Evaluation: From Offline Scores to Decision Risk",
    date: "19 July 2026",
    dateISO: "2026-07-19",
    dateModifiedISO: "2026-07-19",
    excerpt:
      "A research-grounded framework for evaluating predictive models and LLM agents as decision systems under uncertainty, distribution shift, and operational constraints.",
    readTime: "10 min read",
    category: "Machine Learning",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  };

  const content = (
    <div className="content">
      <h2>Abstract</h2>
      <p style={{ margin: "1.5rem 0" }}>
        Production AI evaluation constructs evidence that a versioned system in
        a particular workflow has acceptable decision risk. This article
        develops that claim through expected loss, leakage-resistant offline
        design, calibration, uncertainty, distribution shift, slice analysis,
        and monitoring. It also distinguishes predictive models from
        language-model and agent systems. Deployment is justified only by
        linked evidence whose assumptions and failure response are explicit.
      </p>

      <h2>Research Question</h2>
      <p>
        <strong>
          What evidence is sufficient to prefer deploying an AI system over a
          specified alternative, for a defined population and operating period,
          while bounding consequential failures?
        </strong>{" "}
        The comparator may be an existing human workflow, a deterministic rule,
        another model, or non-deployment. Benchmark improvement is not
        operational value, and average performance is not evidence for every
        case. The evaluation object is the complete system—data, model,
        thresholds or prompts, retrieval, tools, human intervention, and
        downstream action—not merely its learned component.
      </p>

      <h2>Evaluation as Decision Risk</h2>
      <p>
        Let <em>X</em> denote observed context, <em>Y</em> the uncertain state
        relevant to the task, <em>A</em> an action, and{" "}
        <em>π<sub>θ</sub>(A|X)</em> the deployed policy induced by system
        version <em>θ</em>. Given a loss function <em>L(A,Y,X)</em> and target
        deployment distribution <em>P*</em>, its risk is
      </p>
      <p className="research-equation">
        R(π<sub>θ</sub>; P*) = E<sub>(X,Y)∼P*</sub>
        [E<sub>A∼π<sub>θ</sub>(·|X)</sub>[L(A,Y,X)]].
      </p>
      <p>
        Deployment is supported when the estimated risk difference{" "}
        <em>ΔR = R(π<sub>θ</sub>) − R(π<sub>0</sub>)</em> favors the candidate
        over comparator <em>π<sub>0</sub></em>, subject to uncertainty and
        constraints on severe errors, latency, privacy, or subgroup risk.
        Utility is negative loss. Loss must encode asymmetric consequences: a
        missed referral, unnecessary review, and unauthorized action cannot be
        exchanged one-for-one through accuracy. When costs cannot be credibly
        scalarized, use an outcome vector and release gates.
      </p>
      <p>
        Offline data come from <em>P<sub>test</sub></em>, whereas the estimand
        concerns <em>P*</em>; labels may be delayed or selective, and actions
        may change outcomes. Risk is therefore estimated under assumptions, not
        as an intrinsic model property.
      </p>

      <figure className="research-figure">
        <svg
          className="research-figure__svg"
          viewBox="0 0 800 360"
          role="img"
          aria-labelledby="evaluation-pipeline-title evaluation-pipeline-desc"
        >
          <title id="evaluation-pipeline-title">
            Evidence pipeline for production AI evaluation
          </title>
          <desc id="evaluation-pipeline-desc">
            A loop connects decision specification, offline evidence, staged
            deployment, production outcomes, and assumption review.
          </desc>
          <defs>
            <marker
              id="evaluation-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>
          <rect
            className="research-figure__box"
            x="35"
            y="60"
            width="165"
            height="82"
            rx="10"
          />
          <text className="research-figure__label" x="117" y="92" textAnchor="middle">
            Decision specification
          </text>
          <text className="research-figure__muted" x="117" y="117" textAnchor="middle">
            population · action · loss
          </text>
          <rect
            className="research-figure__box"
            x="317"
            y="35"
            width="166"
            height="82"
            rx="10"
          />
          <text className="research-figure__label" x="400" y="67" textAnchor="middle">
            Offline evidence
          </text>
          <text className="research-figure__muted" x="400" y="92" textAnchor="middle">
            estimates · intervals · slices
          </text>
          <rect
            className="research-figure__box"
            x="600"
            y="60"
            width="165"
            height="82"
            rx="10"
          />
          <text className="research-figure__label" x="682" y="92" textAnchor="middle">
            Staged deployment
          </text>
          <text className="research-figure__muted" x="682" y="117" textAnchor="middle">
            shadow · canary · rollback
          </text>
          <rect
            className="research-figure__box"
            x="522"
            y="235"
            width="184"
            height="82"
            rx="10"
          />
          <text className="research-figure__label" x="614" y="267" textAnchor="middle">
            Production outcomes
          </text>
          <text className="research-figure__muted" x="614" y="292" textAnchor="middle">
            labels · incidents · drift
          </text>
          <rect
            className="research-figure__box"
            x="94"
            y="235"
            width="184"
            height="82"
            rx="10"
          />
          <text className="research-figure__label" x="186" y="267" textAnchor="middle">
            Assumption review
          </text>
          <text className="research-figure__muted" x="186" y="292" textAnchor="middle">
            revise · restrict · withdraw
          </text>
          <path
            className="research-figure__line"
            d="M200 84 L315 76"
            markerEnd="url(#evaluation-arrow)"
          />
          <path
            className="research-figure__line"
            d="M484 76 L598 84"
            markerEnd="url(#evaluation-arrow)"
          />
          <path
            className="research-figure__line"
            d="M683 143 L633 232"
            markerEnd="url(#evaluation-arrow)"
          />
          <path
            className="research-figure__line"
            d="M520 276 L280 276"
            markerEnd="url(#evaluation-arrow)"
          />
          <path
            className="research-figure__line"
            d="M154 233 L117 145"
            markerEnd="url(#evaluation-arrow)"
          />
        </svg>
        <figcaption>
          Evaluation is a renewable evidence loop. Production observations test
          the assumptions that made offline estimates relevant; violations lead
          to revision, restriction, or withdrawal rather than silent metric
          drift.
        </figcaption>
      </figure>

      <h2>Offline Evaluation Design</h2>
      <p>
        Begin with a protocol written before final model comparison. Specify
        population, unit of analysis, horizon, comparator, decision rule,
        estimand, failure constraints, and multiplicity policy. Build a
        versioned set from intended traffic, temporal holdouts, rare
        high-consequence cases, and incident or adversarial suites. Split where
        dependence arises—patient, customer, document family, or time—not by
        row when entities recur. Deduplicate corpora and quarantine the final
        set from model or prompt selection; repeated holdout adaptation is
        training.
      </p>
      <p>
        Match uncertainty estimates to sampling: use stratified or cluster
        bootstrap intervals for grouped observations and paired intervals for
        same-case comparisons. Rare-event rates require denominators and
        defensible intervals; zero observed failures is not zero risk. Tune
        thresholds on validation data and evaluate once on held-out data.
        Cost-sensitive analysis is more informative than AUROC at a narrow
        operating range.
      </p>

      <h2>Shift, Calibration, Uncertainty, and Slices</h2>
      <p>
        Dataset shift has distinct forms. Covariate shift changes{" "}
        <em>P(X)</em> while preserving <em>P(Y|X)</em>; label shift changes
        prevalence under stronger invariance assumptions; concept drift changes{" "}
        <em>P(Y|X)</em>. Feature distances detect change but identify neither
        its type nor performance loss; stable marginals can conceal conditional
        change. Stress tests should perturb plausible nuisance factors and use
        temporal or geographic external data where available. Reweighting must
        state overlap and invariance assumptions.
      </p>
      <p>
        Calibration asks whether events predicted with probability <em>p</em>{" "}
        occur at frequency <em>p</em>. Reliability diagrams, proper scoring
        rules such as log loss or Brier score, and calibration error should
        accompany discrimination. Assess calibration near action thresholds and
        within consequential slices: global calibration can conceal local
        error. Post-hoc scaling requires representative validation data and
        reevaluation after shift.
      </p>
      <p>
        Aleatoric uncertainty reflects outcome ambiguity; epistemic uncertainty
        reflects limited knowledge or unfamiliar inputs. Predictive intervals,
        conformal sets, ensembles, and abstention are meaningful only under
        their coverage assumptions. Pre-specify slices through domain and
        hazard analysis: relevant demographic groups, devices, languages,
        sites, missingness patterns, and low-support regions. Report
        denominators and intervals, control exploratory multiplicity, and test
        intersections. Measured slices do not establish safety elsewhere.
      </p>

      <h2>Predictive Models Versus LLMs and Agents</h2>
      <p>
        Predictive ML often maps a stable schema to a score and permits
        outcome-based metrics once labels mature. LLM outputs are open-form,
        stochastic, and history-dependent. Agents also choose tools and alter
        state, making the trajectory—not merely a response—the evaluation unit.
      </p>
      <div className="research-table-wrap">
        <table className="research-table">
          <thead>
            <tr>
              <th>System</th>
              <th>Primary evidence</th>
              <th>Characteristic failure</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Predictive model</td>
              <td>Decision loss, calibration, threshold behavior</td>
              <td>Shifted error rates or miscalibrated action</td>
            </tr>
            <tr>
              <td>LLM application</td>
              <td>Task success, groundedness, rubric reliability</td>
              <td>Unsupported content or unstable instruction following</td>
            </tr>
            <tr>
              <td>Agent</td>
              <td>Trajectory success, tool validity, policy compliance</td>
              <td>Irreversible or unauthorized state transition</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Decompose retrieval systems into coverage, claim support, answer
        quality, and citation validity. Test tool selection, arguments,
        authorization, idempotency, and recovery apart from end-to-end success;
        use deterministic validators for schemas and permissions. Human
        judgments need a rubric, blinding, adjudication, and agreement analysis.
        An LLM judge is an instrument, not ground truth: validate it against
        qualified humans, inspect systematic disagreement, and revalidate after
        changes. Repeat trials for stochastic decoding or environments.
      </p>

      <h2>Online Monitoring as Sequential Evaluation</h2>
      <p>
        Progress from shadow testing to limited canaries and controlled
        expansion with predefined rollback criteria. Randomized experiments
        estimate causal effects when interference, ethics, and power permit;
        before–after comparisons confound model effects with time. Log model,
        feature, prompt, retrieval, tool, policy, and threshold versions needed
        for reconstruction while minimizing personal data.
      </p>
      <p>
        Connect monitoring to the risk model: track data quality, actions and
        abstentions, delayed loss, calibration, incidents, slices, overrides,
        latency, and cost. Predefine alert thresholds, owners, and responses.
        If outcomes appear only for approved or treated cases, naïve estimates
        are policy-biased. Audited random samples, ethical exploration, causal
        adjustment under explicit assumptions, or follow-up may be necessary.
        Add incidents to regression suites while protecting a separate sample
        for less adaptive estimation.
      </p>

      <h2>Assumptions</h2>
      <ul>
        <li>
          Population, comparator, actions, and loss are sufficiently stable and
          specified before evaluation.
        </li>
        <li>
          Evaluation data approximate deployment or support transport through
          overlap and invariance assumptions.
        </li>
        <li>
          Labels measure the intended construct with characterized error, and
          estimates reflect dependence.
        </li>
        <li>
          Logs reconstruct versions, actions, and observable outcomes without
          unacceptable privacy cost.
        </li>
      </ul>

      <h2>Threats to Validity</h2>
      <p>
        <strong>Internal validity:</strong> leakage, duplicate entities,
        selective labels, annotation changes, or unblinded review bias
        estimates. <strong>Construct validity:</strong> benchmarks, preferences,
        and automated judges may not represent utility, support, or harm.{" "}
        <strong>External validity:</strong> historical data may not transport
        across time, sites, languages, interfaces, or strategic behavior.{" "}
        <strong>Statistical conclusion validity:</strong> rare harms,
        correlated trajectories, repeated searches, and underpowered slices
        destabilize claims. Deployment also creates feedback: actions affect
        label availability and may alter <em>P(Y|X)</em>. No finite suite proves
        safety; claims remain bounded by declared conditions.
      </p>

      <h2>Conclusion</h2>
      <p>
        Production evaluation should estimate comparative decision risk, not
        celebrate an isolated score. A defensible program links a pre-specified
        estimand to leakage-resistant offline evidence, calibration and
        uncertainty analysis, consequential slices, staged deployment, and
        versioned monitoring. For LLMs and agents, it must additionally measure
        trajectories, tools, grounding, and judge validity. The output is a
        conditional decision—deploy, restrict, revise, or withdraw—supported by
        evidence whose assumptions and failure modes remain visible.
      </p>

      <h2>References</h2>
      <ol>
        <li>
          National Institute of Standards and Technology (2023).{" "}
          <a href="https://doi.org/10.6028/NIST.AI.100-1">
            Artificial Intelligence Risk Management Framework (AI RMF 1.0)
          </a>.
        </li>
        <li>
          Guo, C., Pleiss, G., Sun, Y., and Weinberger, K. Q. (2017).{" "}
          <a href="https://proceedings.mlr.press/v70/guo17a.html">
            On Calibration of Modern Neural Networks
          </a>. ICML.
        </li>
        <li>
          Ovadia, Y. et al. (2019).{" "}
          <a href="https://proceedings.neurips.cc/paper/2019/hash/8558cb408c1d76621371888657d2eb1d-Abstract.html">
            Can You Trust Your Model&apos;s Uncertainty? Evaluating Predictive
            Uncertainty Under Dataset Shift
          </a>. NeurIPS.
        </li>
        <li>
          Gama, J., Žliobaitė, I., Bifet, A., Pechenizkiy, M., and Bouchachia,
          A. (2014).{" "}
          <a href="https://doi.org/10.1145/2523813">
            A Survey on Concept Drift Adaptation
          </a>. ACM Computing Surveys.
        </li>
        <li>
          Sculley, D. et al. (2015).{" "}
          <a href="https://papers.nips.cc/paper/5656-hidden-technical-debt-in-machine-learning-systems">
            Hidden Technical Debt in Machine Learning Systems
          </a>. NeurIPS.
        </li>
        <li>
          Angelopoulos, A. N., and Bates, S. (2023).{" "}
          <a href="https://doi.org/10.1561/2200000101">
            Conformal Prediction: A Gentle Introduction
          </a>. Foundations and Trends in Machine Learning.
        </li>
        <li>
          Liang, P. et al. (2023).{" "}
          <a href="https://jmlr.org/papers/v24/22-0913.html">
            Holistic Evaluation of Language Models
          </a>. Journal of Machine Learning Research.
        </li>
        <li>
          National Institute of Standards and Technology (2024).{" "}
          <a href="https://doi.org/10.6028/NIST.AI.600-1">
            Artificial Intelligence Risk Management Framework: Generative
            Artificial Intelligence Profile
          </a>.
        </li>
      </ol>
    </div>
  );

  return { ...metadata, content };
};

export default ModelEvaluationPost;
